import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axiosInstance';

// Async thunks
export const fetchTransfers = createAsyncThunk(
  'transfer/fetchTransfers',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) params.append(key, filters[key]);
      });
      
      const response = await axiosInstance.get(`/api/transfer?${params.toString()}`);
      return response.data; // { items, page, limit, total }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfers');
    }
  }
);

export const fetchTransferById = createAsyncThunk(
  'transfer/fetchTransferById',
  async (transferId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/transfer/${transferId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfer');
    }
  }
);

export const createTransfer = createAsyncThunk(
  'transfer/createTransfer',
  async (transferData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/api/transfer', transferData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create transfer');
    }
  }
);

export const updateTransferStatus = createAsyncThunk(
  'transfer/updateTransferStatus',
  async ({ transferId, status, receivedBy }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/api/transfer/${transferId}/status`, {
        status,
        receivedBy
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update transfer status');
    }
  }
);

export const fetchTransferSummary = createAsyncThunk(
  'transfer/fetchTransferSummary',
  async (dateRange = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);
      
      const response = await axiosInstance.get(`/api/transfer/stats/summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfer summary');
    }
  }
);

export const fetchAvailableLocations = createAsyncThunk(
  'transfer/fetchAvailableLocations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/transfer/locations/available');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch available locations');
    }
  }
);

export const fetchTransferProducts = createAsyncThunk(
  'transfer/fetchTransferProducts',
  async ({ sourceType, sourceId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/transfer/products/${sourceType}/${sourceId}`);
      if (response.data.length > 0) {
      }
      return { sourceType, sourceId, products: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch transfer products');
    }
  }
);

// Initial state
const initialState = {
  transfers: [],
  pagination: { page: 1, limit: 20, total: 0 },
  currentTransfer: null,
  transferSummary: null,
  availableLocations: null,
  transferProducts: {},
  loading: false,
  error: null,
  success: null,
};

// Slice
const transferSlice = createSlice({
  name: 'transfer',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setCurrentTransfer: (state, action) => {
      state.currentTransfer = action.payload;
    },
    clearCurrentTransfer: (state) => {
      state.currentTransfer = null;
    },
    clearTransferSummary: (state) => {
      state.transferSummary = null;
    },
    clearAvailableLocations: (state) => {
      state.availableLocations = null;
    },
    clearTransferProducts: (state) => {
      state.transferProducts = {};
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch transfers
      .addCase(fetchTransfers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) {
          // backward compatibility if server returns array
          state.transfers = action.payload;
          state.pagination = { page: 1, limit: action.payload.length, total: action.payload.length };
        } else {
          state.transfers = action.payload.items || [];
          state.pagination = { page: action.payload.page, limit: action.payload.limit, total: action.payload.total };
        }
      })
      .addCase(fetchTransfers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch transfer by ID
      .addCase(fetchTransferById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTransfer = action.payload;
      })
      .addCase(fetchTransferById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create transfer
      .addCase(createTransfer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers.unshift(action.payload);
        state.success = 'Transfer created successfully';
      })
      .addCase(createTransfer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update transfer status
      .addCase(updateTransferStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransferStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.transfers.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.transfers[index] = action.payload;
        }
        if (state.currentTransfer && state.currentTransfer.id === action.payload.id) {
          state.currentTransfer = action.payload;
        }
        state.success = 'Transfer status updated successfully';
      })
      .addCase(updateTransferStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch transfer summary
      .addCase(fetchTransferSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.transferSummary = action.payload;
      })
      .addCase(fetchTransferSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch available locations
      .addCase(fetchAvailableLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.availableLocations = action.payload;
      })
      .addCase(fetchAvailableLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch transfer products
      .addCase(fetchTransferProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransferProducts.fulfilled, (state, action) => {
        state.loading = false;
        const key = `${action.payload.sourceType}_${action.payload.sourceId}`;
        state.transferProducts[key] = action.payload.products;
      })
      .addCase(fetchTransferProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setCurrentTransfer,
  clearCurrentTransfer,
  clearTransferSummary,
  clearAvailableLocations,
  clearTransferProducts,
} = transferSlice.actions;

export default transferSlice.reducer; 